import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Receipt,
  RefreshCw,
  FileText,
  Search,
  Download
} from "lucide-react";
import { Input } from "../../components/ui/input";

const PortalComprobantesPage = () => {
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [comprobantes, setComprobantes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchComprobantes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal/mis-comprobantes`, { headers });
      setComprobantes(res.data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComprobantes();
  }, []);

  const filteredComprobantes = comprobantes.filter((c) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.nro_recibo || "").toLowerCase().includes(term) ||
      (c.nro_planilla || "").toLowerCase().includes(term) ||
      (c.oficina || "").toLowerCase().includes(term)
    );
  });

  const getEstatusColor = (estatus) => {
    switch (estatus) {
      case "Pagado": return "bg-green-100 text-green-700";
      case "Pendiente": return "bg-yellow-100 text-yellow-700";
      case "Anulado": return "bg-red-100 text-red-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const totalPagado = comprobantes
    .filter(c => c.estatus === "Pagado")
    .reduce((acc, c) => acc + (c.monto || 0), 0);

  const totalPendiente = comprobantes
    .filter(c => c.estatus === "Pendiente")
    .reduce((acc, c) => acc + (c.monto || 0), 0);

  return (
    <div className="space-y-6" data-testid="portal-comprobantes-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Comprobantes de Pago</h2>
          <p className="text-sm text-slate-500">Consulte el historial de sus comprobantes y recibos</p>
        </div>
        <Button
          onClick={fetchComprobantes}
          variant="outline"
          size="sm"
          data-testid="btn-refresh-comprobantes"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Comprobantes</p>
                <p className="text-xl font-bold">{comprobantes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Receipt className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Pagado</p>
                <p className="text-xl font-bold text-green-700">
                  Bs. {totalPagado.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Receipt className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Pendiente</p>
                <p className="text-xl font-bold text-amber-700">
                  Bs. {totalPendiente.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Listado de Comprobantes
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por recibo, planilla..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 text-sm"
                data-testid="search-comprobantes"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filteredComprobantes.length === 0 ? (
            <div className="text-center py-8 text-slate-500" data-testid="no-comprobantes">
              <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No tiene comprobantes de pago registrados.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-2 px-3 font-medium">Nro. Recibo</th>
                    <th className="text-left py-2 px-3 font-medium">Fecha</th>
                    <th className="text-left py-2 px-3 font-medium">Nro. Planilla</th>
                    <th className="text-left py-2 px-3 font-medium">Oficina</th>
                    <th className="text-right py-2 px-3 font-medium">Monto (Bs.)</th>
                    <th className="text-left py-2 px-3 font-medium">Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredComprobantes.map((c) => (
                    <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`comprobante-${c.nro_recibo}`}>
                      <td className="py-2 px-3 font-mono font-medium">{c.nro_recibo}</td>
                      <td className="py-2 px-3">{c.fecha}</td>
                      <td className="py-2 px-3 font-mono">{c.nro_planilla}</td>
                      <td className="py-2 px-3">{c.oficina}</td>
                      <td className="py-2 px-3 text-right font-medium">
                        {(c.monto || 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-3">
                        <Badge className={getEstatusColor(c.estatus)}>
                          {c.estatus}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalComprobantesPage;
